import axios from 'axios';

export const getNestedValue = (key, obj) =>
  key
    .split('.')
    .reduce((o, v) => (o && typeof o[v] !== 'undefined' ? o[v] : null), obj);

export const getFullName = (user) => {
  return (
    (user?.lastName || '') +
    (user?.lastName && user?.firstName ? ' ' : '') +
    (user?.firstName || '')
  );
};

export const capitalizeFirstLetter = (string) => {
  if (typeof string !== 'string') return string;
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const downloadFile = async (downloadUrl) => {
  var link;
  try {
    const response = await axios({
      responseType: 'blob',
      ...(typeof downloadUrl === 'string'
        ? {
            method: 'GET',
            url: downloadUrl
          }
        : downloadUrl)
    });

    const attachment = response.headers['content-disposition'];
    const fileNameIdx = attachment.indexOf('filename="') + 10;
    const filename = attachment.substring(
      fileNameIdx,
      attachment.indexOf('"', fileNameIdx)
    );

    const decodedFileName = decodeURIComponent(filename);

    const url = window.URL.createObjectURL(new Blob([response.data]));
    link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', decodedFileName);
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
    return [true, null];
  } catch (error) {
    // https://stackoverflow.com/a/62988821
    if (error?.response?.request?.responseType === 'blob') {
      const isJson = error.response.data?.type === 'application/json';
      error.response.data = (await error?.response?.data?.text()) || '';

      if (isJson && error.response.data) {
        error.response.data = JSON.parse(error.response.data);
      }
    }

    return [null, error.response.data];
  } finally {
    link?.remove();
  }
};

export const getBaseRoute = (user) => {
  if (!user) return '/apply';
  if (user.isRoot) return '/admin/tenants';
  if (user.tenants?.length > 0) return `/tenants/${user.tenants[0].tenantId}`;
  return '/apply';
};

export const range = (from, to, step = 1) => {
  let i = from;
  const range = [];

  while (i <= to) {
    range.push(i);
    i += step;
  }

  return range;
};

export const buildFilterTemplate = (obj) => {
  if (Array.isArray(obj)) {
    return [];
  } else if (typeof obj === 'object' && obj !== null) {
    const result = {};
    for (const key in obj) {
      result[key] = buildFilterTemplate(obj[key]);
    }
    return result;
  } else if (typeof obj === 'boolean') {
    return obj;
  } else {
    return null;
  }
};

export const convertEmptyStringsToNull = (obj) => {
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) {
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        convertEmptyStringsToNull(obj[key]);
      } else if (obj[key] === '') {
        obj[key] = null;
      }
    }
  }
  return obj;
};

export function flattenObject(obj) {
  return Object.keys(obj).reduce((acc, key) => {
    if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
      const nestedObj = flattenObject(obj[key]);
      Object.assign(acc, nestedObj);
    } else {
      acc[key] = obj[key];
    }
    return acc;
  }, {});
}

export const replaceLocation = (url) => {
  if (!url) return;
  window.location.replace(url);
};

export function getSemesterInterval(semesters) {
  if (!semesters) return '';

  let semesterStartDate = '';
  let semesterEndDate = '';

  const { startDate = null, endDate = null } = semesters[0] || {};

  if (startDate) {
    semesterStartDate = new Date(startDate);
  }
  if (endDate) {
    semesterEndDate = new Date(endDate);
  }

  return { semesterStartDate, semesterEndDate };
}

export function searchFromValues(term, ...values) {
  if (
    !term ||
    typeof term !== 'string' ||
    !Array.isArray(values) ||
    !values.length
  ) {
    return true;
  }

  const searchTerm = term.toLowerCase();
  return values.some((v) => {
    if (typeof v !== 'string') return false;
    return v.toLowerCase().includes(searchTerm);
  });
}

export function getLocalDatetime(datetime) {
  if (!datetime) return '';

  const d = new Date(datetime);
  const yyyy = d.getFullYear().toString().padStart(4, '0');
  const mm = (d.getMonth() + 1).toString().padStart(2, '0');
  const dd = d.getDate().toString().padStart(2, '0');
  const hh = d.getHours().toString().padStart(2, '0');
  const MM = d.getMinutes().toString().padStart(2, '0');

  return `${yyyy}-${mm}-${dd}T${hh}:${MM}`;
}

export function isFormEnabled(processSettings) {
  if (
    processSettings?.formEnabled == 'scheduled' &&
    processSettings?.formEnabledEnd
  ) {
    return new Date() < new Date(processSettings?.formEnabledEnd);
  }

  return processSettings?.formEnabled !== 'false';
}

export const scrollIntoFirstInvalidItem = (parentElement) => {
  if (!parentElement) return;

  let element = parentElement.elements?.item?.(0);
  for (var i = 1; element; element = parentElement.elements.item(i++)) {
    if (
      !element?.validity?.valid ||
      element?.classList?.contains('is-invalid')
    ) {
      element?.scrollIntoView({ behaviour: 'smooth', block: 'center' });
      element.focus();
      break;
    }
  }
};

export const setNestedValue = (path, obj, value) => {
  const keys = path.split('.');
  const lastKey = keys.pop();
  const lastObj = keys.reduce((acc, key) => {
    if (!acc[key] || typeof acc[key] !== 'object') {
      acc[key] = {};
    }
    return acc[key];
  }, obj);
  lastObj[lastKey] = value;
};

export const formatEUR = (cents) => {
  if (cents == null) return '—';
  const euros = Number(cents) / 100;
  return euros.toLocaleString('fi-FI', {
    style: 'currency',
    currency: 'EUR'
  });
};

//luo lukukausioptiot opiskeluoikeuden aikavälin mukaan
export const buildSemesterOptions = ({ startISO, endISO, localize }) => {
  const d = (iso) => (iso ? new Date(iso) : null);
  const start = d(startISO);
  const end = d(endISO);
  if (!start || !end) return [];

  const opts = [];
  const startYear = start.getFullYear();
  const endYear = end.getFullYear();

  // Semestereiden "alkupäivät" vertailuun
  const semStartDate = (year, sem) =>
    new Date(year, sem === 'spring' ? 0 : 7, 1); // spring: Jan 1, autumn: Aug 1

  for (let y = startYear; y <= endYear; y++) {
    // spring
    const springDate = semStartDate(y, 'spring');
    if (
      springDate >= new Date(start.getFullYear(), start.getMonth(), 1) &&
      springDate <= end
    ) {
      opts.push({
        value: { semester: 'spring', year: y },
        label: `${localize('semester.spring')} ${y}`
      });
    }
    // autumn
    const autumnDate = semStartDate(y, 'autumn');
    if (
      autumnDate >= new Date(start.getFullYear(), start.getMonth(), 1) &&
      autumnDate <= end
    ) {
      opts.push({
        value: { semester: 'autumn', year: y },
        label: `${localize('semester.autumn', { editable: false })} ${y}`
      });
    }
  }

  // lajittelu kalenterijärjestykseen (spring ennen autumn saman vuoden sisällä)
  return opts.sort((a, b) =>
    a.value.year !== b.value.year
      ? a.value.year - b.value.year
      : a.value.semester === 'spring' && b.value.semester === 'autumn'
        ? -1
        : 1
  );
};

export function sortDefaultPrices(defaultPrices) {
  return [...defaultPrices].sort((a, b) => {
    const educationType =
      a.educationTypeCodeKey && !b.educationTypeCodeKey
        ? -1
        : !a.educationTypeCodeKey && b.educationTypeCodeKey
          ? 1
          : 0;

    const degreeProgrammes =
      (Array.isArray(b.degreeProgrammeIds) ? b.degreeProgrammeIds.length : 0) -
      (Array.isArray(a.degreeProgrammeIds) ? a.degreeProgrammeIds.length : 0);

    const result = educationType + degreeProgrammes;

    if (result === 0) {
      return (
        new Date(b.rightToStudyStartGte || 0) -
        new Date(a.rightToStudyStartGte || 0)
      );
    }

    return result;
  });
}

export function sortProductCodes(productCodes) {
  return [...productCodes].sort((a, b) => {
    const degreeProgrammes =
      (Array.isArray(b.degreeProgrammeIds) ? b.degreeProgrammeIds.length : 0) -
      (Array.isArray(a.degreeProgrammeIds) ? a.degreeProgrammeIds.length : 0);

    if (degreeProgrammes === 0) {
      return (b.year || 0) - (a.year || 0);
    }

    return degreeProgrammes;
  });
}

export function getSemesterValue(semester) {
  return `${semester.year}-${semester.semester === 'spring' ? 1 : 2}`;
}
